import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';

const Sidebar = ({ user, onLogout, isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout();
      onLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navGroups = [
    {
      title: 'OVERVIEW',
      items: [
        { path: '/dashboard', icon: 'bi-grid-1x2-fill', label: 'Dashboard' }
      ]
    },
    {
      title: 'CONTENT MANAGEMENT',
      items: [
        { path: '/products', icon: 'bi-box-seam-fill', label: 'Products' },
        { path: '/livestreams', icon: 'bi-broadcast', label: 'Live Streams' },
      ]
    },
    {
      title: 'BUSINESS',
      items: [
        { path: '/orders', icon: 'bi-receipt', label: 'Orders' },
        { path: '/payments', icon: 'bi-wallet-fill', label: 'Earnings' },
        { path: '/credits', icon: 'bi-credit-card-fill', label: 'Credits' }
      ]
    }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <div
        className={`flex flex-col h-screen fixed top-0 z-50 transition-all duration-300 ease-in-out
          ${isCollapsed
            ? '-left-full lg:left-0 lg:w-20'
            : 'left-0 w-[280px] lg:w-64'}
        `}
        style={{ backgroundColor: '#0a0f1e', borderRight: '1px solid #1e2a47' }}
      >
        {/* Brand */}
        <div className="p-4 mb-2 relative">
          <div className={`flex items-center gap-3 ${isCollapsed ? 'lg:justify-center' : 'justify-start px-2'}`}>
            <img
              src="/whatnewlogo.png"
              alt="WhatNew"
              className="w-10 h-10 object-contain"
            />
            <div className={`transition-opacity duration-300 ${isCollapsed ? 'lg:hidden' : 'block'}`}>
              <div className="text-white font-bold text-lg tracking-tight">WhatNew</div>
              <div className="text-xs font-bold uppercase tracking-wider" style={{ color: '#ef4444' }}>Seller</div>
            </div>
          </div>

          {/* Toggle Button (Desktop Only) */}
          <button
            onClick={onToggle}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-red-500 text-white hidden lg:flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-50"
            style={{ border: '2px solid #0a0f1e' }}
          >
            <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'left'} text-xs`}></i>
          </button>

          {/* Close Button (Mobile Only) */}
          <button
            onClick={onToggle}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94a3b8] lg:hidden hover:text-white transition-colors"
          >
            <i className="bi bi-x-lg text-xl"></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 overflow-y-auto mt-4">
          {navGroups.map((group, idx) => (
            <div key={idx} className="mb-6">
              {!isCollapsed && (
                <div className="px-3 mb-2 text-[10px] font-bold tracking-[0.2em] uppercase transition-opacity duration-300" style={{ color: '#64748b' }}>
                  {group.title}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                    title={isCollapsed ? item.label : ''}
                    className={`flex items-center rounded-lg transition-all duration-200 group ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}`}
                    style={{
                      backgroundColor: isActiveRoute(item.path) ? '#ef4444' : 'transparent',
                      color: isActiveRoute(item.path) ? '#ffffff' : '#94a3b8',
                    }}
                  >
                    <i className={`bi ${item.icon} ${isCollapsed ? 'text-xl' : 'text-base'} transition-transform duration-200 group-hover:scale-110`}></i>
                    {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Status & Sign Out */}
        <div className="p-3 mt-auto">
          {!isCollapsed && (
            <div className="mb-3 px-3 text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#64748b' }}>
              STATUS
            </div>
          )}

          <div className={`flex items-center rounded-lg mb-3 ${isCollapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'}`} style={{ backgroundColor: '#161e35' }}>
            <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]"></div>
            {!isCollapsed && (
              <div className="flex-1">
                <div className="text-white text-[11px] font-bold">Online</div>
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sign Out' : ''}
            className={`flex items-center rounded-lg transition-all duration-200 text-[#94a3b8] hover:bg-white/5 hover:text-white w-full ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}`}
          >
            <i className="bi bi-box-arrow-left text-base"></i>
            {!isCollapsed && <span className="text-sm font-medium">Sign Out</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

